import React, { useState, useEffect } from 'react';
import PhoneInput, { isValidPhone } from '@/components/common/PhoneInput';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { CheckCircle2, ArrowRight, ArrowLeft, Shield, Sparkles, XCircle, X, UserCheck, CalendarCheck, ExternalLink, MessageCircle } from 'lucide-react';
import { useReferral } from '@/hooks/useReferral';
import { useFormToken } from '@/hooks/useFormToken';
import FreedySDRChat from '@/components/funnel/FreedySDRChat';

import { BACKEND_URL } from '@/config/api';

const API = BACKEND_URL;
// Agenda de llamadas tras el test: la de Toni (02/11/2026). El botón de agenda sale SIEMPRE.
// Manda la de Ajustes del test (GET /api/funnel/config); esta es la de respaldo si no responde.
const DEFAULT_BOOKING_URL = 'https://calendly.com/toni-freedomhomes/30min';

// indexOf → null helper (so a missing answer becomes null, not -1, for the scorer)
const optIndex = (arr, val) => {
  const i = (arr || []).indexOf(val);
  return i < 0 ? null : i;
};

/* ─── BILINGUAL TEXTS ───
   Las preguntas y opciones del test (q1-q5) están también en backend/utils/funnel_scoring.py (QUESTIONS),
   que el panel admin usa para enseñar cada respuesta en texto (se guardan índices). Si cambias un texto o
   una opción aquí, cámbialo allí también; nunca reordenes opciones: añade las nuevas al final. */
const QF_TEXTS = {
  es: {
    title: 'Test de idoneidad',
    intro: {
      heading: 'El acceso a Freedom Homes Club no es automático.',
      p1: 'No porque queramos poner barreras — sino porque el club funciona porque todos los que están dentro tienen el mismo nivel de compromiso y criterio.',
      p2: 'Este test nos ayuda a entender si somos el lugar adecuado para ti. Son 5 preguntas, menos de 2 minutos. Al terminar, eliges día y hora para una llamada con el equipo.',
      cta: 'Empezar el test',
    },
    q1: {
      question: '¿Qué te trae a Freedom Homes Club?',
      options: [
        'Acceder a oportunidades inmobiliarias y aprender a analizarlas',
        'Certificarme como Broker Digital',
        'Formar parte de la comunidad y los eventos',
        'Aún estoy explorando',
      ],
    },
    q2: {
      question: '¿Cuál es tu perfil?',
      options: [
        'Empresario o directivo',
        'Profesional inmobiliario',
        'Ventas o emprendimiento',
        'Otro',
      ],
    },
    q3: {
      question: '¿Cuánto tiempo puedes dedicar a formarte?',
      options: [
        'Menos de 2 h/semana',
        'Entre 2 y 5 h/semana',
        'Más de 5 h/semana',
      ],
    },
    q4: {
      question: '¿Tendrías disponibilidad para asistir al Bootcamp presencial en España?',
      options: [
        'Resido en España o puedo desplazarme sin problema',
        'Me interesa, pero necesitaría valorar la logística',
        'Por ahora prefiero la formación online',
      ],
    },
    q5: {
      heading: 'Antes de continuar, una cosa importante:',
      p1: 'Freedom Homes Club es un club privado de formación y comunidad.',
      strong: 'La cuota da acceso al club: formación, sesiones, comunidad, eventos y el área privada de oportunidades.',
      p2: 'La membresía no da derechos sobre ningún activo: si más adelante participas en alguna oportunidad, será una decisión aparte, voluntaria y con su propia documentación.',
      p3: 'La certificación FHC es privada e interna del club, sin equivalencia académica oficial.',
      p4: 'Si entiendes y aceptas este marco, estamos en la misma página.',
      confirmOptions: ['Entendido, quiero continuar', 'Prefiero no continuar'],
    },
    rejected: {
      heading: 'Sin problema',
      desc: 'Agradecemos tu honestidad. Si en el futuro cambian tus circunstancias, aquí estaremos.',
    },
    final: {
      heading: 'Casi listo.',
      desc: 'Último paso: déjanos tus datos y, al enviar el test, podrás elegir día y hora para tu llamada con el equipo.',
      invitedBy: 'Has sido invitado por',
      nombre: 'Nombre', nombrePh: 'Tu nombre',
      apellidos: 'Apellidos', apellidosPh: 'Tus apellidos',
      whatsapp: 'WhatsApp', email: 'Email',
      horarioLabel: '¿Cuándo prefieres que te contactemos?',
      horarioOptions: ['Mañana', 'Tarde', 'Sin preferencia'],
      policyText: 'He leído y acepto la',
      policyLink: 'política de privacidad',
      contactText: 'Autorizo a que el equipo de Freedom Homes Club se ponga en contacto conmigo',
      submit: 'Enviar test',
      submitting: 'Enviando...',
    },
    success: {
      heading: 'Test recibido',
      desc: 'Un miembro del equipo revisará tu perfil y te contactará lo antes posible con toda la información relevante para tu caso.',
    },
    buttons: { back: 'Anterior', next: 'Siguiente', close: 'Cerrar' },
    errors: {
      checkboxes: 'Debes aceptar ambas casillas',
      horario: 'Selecciona tu preferencia de contacto',
    },
  },
  en: {
    title: 'Suitability test',
    intro: {
      heading: 'Access to Freedom Homes Club is not automatic.',
      p1: 'Not because we want to create barriers — but because the club works because everyone inside shares the same level of commitment and criteria.',
      p2: 'This test helps us understand if we are the right fit for you. 5 questions, less than 2 minutes. When you finish, you pick a day and time for a call with the team.',
      cta: 'Start the test',
    },
    q1: {
      question: 'What brings you to Freedom Homes Club?',
      options: [
        'Accessing real estate opportunities and learning to analyse them',
        'Getting certified as a Digital Broker',
        'Being part of the community and the events',
        "I'm still exploring",
      ],
    },
    q2: {
      question: 'What is your profile?',
      options: [
        'Business owner or executive',
        'Real estate professional',
        'Sales or entrepreneurship',
        'Other',
      ],
    },
    q3: {
      question: 'How much time can you dedicate to training?',
      options: [
        'Less than 2 h/week',
        'Between 2 and 5 h/week',
        'More than 5 h/week',
      ],
    },
    q4: {
      question: 'Would you be available to attend the in-person Bootcamp in Spain?',
      options: [
        'I live in Spain or can travel without issue',
        "I'm interested, but I would need to evaluate the logistics",
        'For now I prefer online training',
      ],
    },
    q5: {
      heading: 'Before continuing, one important thing:',
      p1: 'Freedom Homes Club is a private club for training and community.',
      strong: 'The fee gives access to the club: training, sessions, community, events and the private opportunities area.',
      p2: 'Membership grants no rights over any asset: if you later take part in an opportunity, it will be a separate, voluntary decision with its own documentation.',
      p3: 'The FHC certification is private and internal to the club, with no official academic equivalence.',
      p4: 'If you understand and accept this framework, we are on the same page.',
      confirmOptions: ['Understood, I want to continue', 'I prefer not to continue'],
    },
    rejected: {
      heading: 'No problem',
      desc: "We appreciate your honesty. If your circumstances change in the future, we'll be here.",
    },
    final: {
      heading: 'Almost there.',
      desc: 'Last step: leave your details and, when you submit the test, you can pick a day and time for your call with the team.',
      invitedBy: 'You were invited by',
      nombre: 'First name', nombrePh: 'Your first name',
      apellidos: 'Last name', apellidosPh: 'Your last name',
      whatsapp: 'WhatsApp', email: 'Email',
      horarioLabel: 'When do you prefer we contact you?',
      horarioOptions: ['Morning', 'Afternoon', 'No preference'],
      policyText: 'I have read and accept the',
      policyLink: 'privacy policy',
      contactText: 'I authorize the Freedom Homes Club team to contact me',
      submit: 'Submit test',
      submitting: 'Submitting...',
    },
    success: {
      heading: 'Test received',
      desc: 'A team member will review your profile and contact you as soon as possible with all relevant information for your case.',
    },
    buttons: { back: 'Back', next: 'Next', close: 'Close' },
    errors: {
      checkboxes: 'You must accept both checkboxes',
      horario: 'Please select your contact preference',
    },
  },
};

export default function QualificationFormModal({ isOpen, onClose, source = 'general', lang = 'es' }) {
  const qft = QF_TEXTS[lang] || QF_TEXTS.es;

  // Con el test abierto se oculta la burbuja del chat público (tapaba los botones del modal en móvil)
  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.classList.add('fhc-modal-open');
    return () => document.body.classList.remove('fhc-modal-open');
  }, [isOpen]);

  // Flow: intro → q1 → q2 → q3 → q4 → q5 → final
  const [step, setStep] = useState('intro');
  const [personal, setPersonal] = useState({ nombre: '', apellidos: '', whatsapp: '', email: '' });
  const [answers, setAnswers] = useState({});
  const [horario, setHorario] = useState('');
  const [acceptPolicy, setAcceptPolicy] = useState(false);
  const [acceptContact, setAcceptContact] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  // When set, the modal switches from the form to the Freedy SDR chat.
  const [sdrLead, setSdrLead] = useState(null); // { leadId, temperature, profile }
  const formToken = useFormToken();  // defensa frente a bots (backend/antibot.py)
  const [bookingClicked, setBookingClicked] = useState(false);
  const [bookingUrl, setBookingUrl] = useState(DEFAULT_BOOKING_URL);
  useEffect(() => {
    axios.get(`${API}/api/funnel/config`)
      .then((r) => { const u = r.data?.booking_url; if (u && /^https:\/\//.test(u)) setBookingUrl(u); })
      .catch(() => {});
  }, []);
  // El enlace abre la agenda en otra pestaña (lo hace el <a>); aquí solo se registra la intención.
  const onBookingClick = () => {
    setBookingClicked(true);
    if (sdrLead?.leadId) axios.post(`${API}/api/funnel/lead/${sdrLead.leadId}/booking-click`).catch(() => {});
  };
  const { referralCode, affiliateName } = useReferral();

  const FLOW = ['intro', 'q1', 'q2', 'q3', 'q4', 'q5', 'final'];

  const getNextStep = (current) => FLOW[FLOW.indexOf(current) + 1];
  const getPrevStep = (current) => FLOW[FLOW.indexOf(current) - 1];

  const questionSteps = FLOW.filter(s => s !== 'intro');
  const questionIdx = questionSteps.indexOf(step);
  const displayStep = step === 'intro' ? 0 : questionIdx + 1;
  const displayTotal = questionSteps.length;
  const progress = step === 'rejected' ? 100 : step === 'intro' ? 0 : (displayStep / displayTotal) * 100;

  const goNext = () => setStep(getNextStep(step));
  const goBack = () => setStep(getPrevStep(step));

  const handleAnswer = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
    // Rejection option is always the second confirm option (index 1) — language-agnostic
    if (key === 'confirmacion' && value === qft.q5.confirmOptions[1]) {
      setTimeout(() => setStep('rejected'), 350);
      return;
    }
    const stepMap = { intencion: 'q1', perfil: 'q2', tiempo: 'q3', disponibilidad: 'q4', confirmacion: 'q5' };
    const nextStep = getNextStep(stepMap[key]);
    if (nextStep) setTimeout(() => setStep(nextStep), 350);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptPolicy || !acceptContact) { toast.error(qft.errors.checkboxes); return; }
    if (!horario) { toast.error(qft.errors.horario); return; }
    if (!isValidPhone(personal.whatsapp)) { toast.error(lang === 'en' ? 'Please enter a valid phone number' : 'Revisa el número de teléfono'); return; }
    setLoading(true);

    // Map the selected option TEXT back to its INDEX so scoring is language-agnostic.
    // Field names must match QuizAnswers in backend/routes/funnel.py.
    const quizAnswers = {
      profile_choice: optIndex(qft.q1.options, answers.intencion),
      background_choice: optIndex(qft.q2.options, answers.perfil),
      time_choice: optIndex(qft.q3.options, answers.tiempo),
      availability_choice: optIndex(qft.q4.options, answers.disponibilidad),
      confirmed: answers.confirmacion === qft.q5.confirmOptions[0],
    };
    const fullName = `${personal.nombre} ${personal.apellidos}`.trim();

    try {
      const token = await formToken();
      // 1) Funnel lead — primary path. Returns leadId + scoring → drives the SDR.
      const { data } = await axios.post(`${API}/api/funnel/lead`, {
        name: fullName,
        email: personal.email,
        whatsapp: personal.whatsapp,
        answers: quizAnswers,
        source: `quiz-${source}`,
        lang,
        form_token: token || undefined,
        ...(referralCode ? { referral_code: referralCode } : {}),
      });

      // 2) Keep feeding the legacy /api/leads pipeline (brokers' "Mis Contactos").
      //    Best-effort — duplicate-email errors here must NOT block the funnel.
      axios.post(`${API}/api/leads`, {
        name: fullName,
        email: personal.email,
        whatsapp: personal.whatsapp,
        source,
        qualification: { nombre: personal.nombre, apellidos: personal.apellidos, ...answers, horario_contacto: horario },
        form_token: token || undefined,
        ...(referralCode ? { referral_code: referralCode } : {}),
      }).catch(() => {});

      // 3) Hand off to Freedy SDR (in-modal) instead of redirecting away.
      setSdrLead({ leadId: data.lead_id, temperature: data.temperature, profile: data.profile });
    } catch (err) {
      const d = err.response?.data?.detail;
      if (!err.response) {
        toast.error(lang === 'en' ? 'Error submitting the application' : 'Error al enviar la solicitud');
      } else {
        toast.error(typeof d === 'string' ? d : (lang === 'en' ? 'Please check the form details' : 'Revisa los datos del formulario'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // En el chat de Freedy la solicitud ya está enviada: al cerrar, que quede constancia
    if (sdrLead && !success) {
      toast.success(lang === 'en'
        ? (bookingClicked ? 'Test received. See you on the call.' : 'Test received. If you have not booked your call yet, we will get in touch.')
        : (bookingClicked ? 'Test recibido. Nos vemos en la llamada.' : 'Test recibido. Si aún no has agendado tu llamada, te escribiremos.'));
    }
    setSuccess(false);
    setSdrLead(null);
    setBookingClicked(false);
    setStep('intro');
    setPersonal({ nombre: '', apellidos: '', whatsapp: '', email: '' });
    setAnswers({});
    setHorario('');
    setAcceptPolicy(false);
    setAcceptContact(false);
    onClose();
  };

  const OptionBtn = ({ selected, onClick, children, testId }) => (
    <button onClick={onClick} data-testid={testId}
      className={`w-full text-left px-4 py-3.5 border-2 transition-all duration-200 font-body text-sm group ${
        selected ? 'border-[#D4AF37] bg-[#D4AF37]/5 text-[#0F172A] shadow-sm' : 'border-slate-100 hover:border-slate-200 text-slate-600 hover:bg-slate-50'
      }`}>
      <span className="flex items-center gap-3">
        <span className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
          selected ? 'border-[#D4AF37] bg-[#D4AF37]' : 'border-slate-300 group-hover:border-slate-400'
        }`}>{selected && <CheckCircle2 className="w-3 h-3 text-white" />}</span>
        {children}
      </span>
    </button>
  );

  const BackBtn = () => (
    <Button onClick={goBack} variant="outline" className="h-12 px-4 sm:px-6 rounded-none border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-2" data-testid="q-back">
      <ArrowLeft className="w-4 h-4" /> {qft.buttons.back}
    </Button>
  );

  const NextBtn = () => (
    <Button onClick={goNext} className="flex-1 h-12 bg-[#0F172A] text-white hover:bg-[#1E293B] rounded-none inline-flex items-center justify-center gap-2 font-semibold animate-in fade-in duration-300" data-testid="q-next">
      {qft.buttons.next} <ArrowRight className="w-4 h-4" />
    </Button>
  );

  const QuestionStep = ({ num, question, answerKey, options }) => (
    <div className="space-y-4" data-testid={`form-${answerKey}`}>
      <p className="font-body text-sm font-semibold text-[#0F172A]">
        <span className="inline-flex items-center justify-center w-6 h-6 bg-[#0F172A] text-white text-xs rounded-full mr-2">{num}</span>
        {question}
      </p>
      <div className="space-y-2">
        {options.map((opt, i) => (
          <OptionBtn key={i} selected={answers[answerKey] === opt} onClick={() => handleAnswer(answerKey, opt)} testId={`q-${answerKey}-${i}`}>{opt}</OptionBtn>
        ))}
      </div>
      <div className="flex gap-3 pt-3">
        <BackBtn />
        {answers[answerKey] && <NextBtn />}
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl p-0 rounded-none border-0 max-h-[92vh] overflow-hidden shadow-2xl" data-testid="qualification-modal">

        {sdrLead ? (
          // ── Tras el test: agenda SIEMPRE arriba (decisión de Samuel, 10/10/2026) y Freedy debajo para dudas ──
          <div className="bg-[#0F172A] max-h-[92vh] overflow-y-auto">
            <div className="bg-[#0a1628] px-5 sm:px-6 pt-5 pb-4 pr-12 relative border-b border-white/[0.06]">
              <button type="button" onClick={handleClose} aria-label={qft.buttons.close} className="absolute top-2 right-2 w-10 h-10 flex items-center justify-center text-white/40 hover:text-white transition-colors" data-testid="sdr-close-x">
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <div>
                  <DialogHeader className="p-0 space-y-0 text-left">
                    <DialogTitle className="font-heading text-lg text-white">
                      {lang === 'en' ? 'Test completed' : 'Test completado'}
                    </DialogTitle>
                    <DialogDescription className="font-body text-white/50 text-xs">
                      {lang === 'en' ? 'Thank you. One step left.' : 'Gracias. Solo queda un paso.'}
                    </DialogDescription>
                  </DialogHeader>
                </div>
              </div>
            </div>

            {/* Siguiente paso: agenda (visible sin hacer scroll, también en móvil) */}
            <div className="px-5 sm:px-6 pt-5" data-testid="post-test-booking">
              <div className="rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.08] p-4 sm:p-5">
                <p className="font-body text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">
                  {lang === 'en' ? 'Next step' : 'Siguiente paso'}
                </p>
                <p className="font-heading text-xl text-white mt-1">
                  {lang === 'en' ? 'Book your call' : 'Agenda tu llamada'}
                </p>
                <p className="font-body text-sm text-white/65 mt-1.5 leading-relaxed">
                  {lang === 'en'
                    ? 'A short call with the team to get to know you, answer your questions and explain which level fits you. The calendar opens in a new tab.'
                    : 'Una llamada breve con el equipo para conocerte, resolver tus dudas y explicarte qué nivel encaja contigo. La agenda se abre en otra pestaña.'}
                </p>
                <a href={bookingUrl} target="_blank" rel="noopener noreferrer" onClick={onBookingClick} data-testid="post-test-booking-btn"
                  className="mt-4 flex h-14 w-full items-center justify-center gap-2 bg-[#D4AF37] px-4 text-base font-semibold text-[#0F172A] hover:bg-[#C5A028] transition-colors">
                  <CalendarCheck className="w-5 h-5" /> {lang === 'en' ? 'Pick a day and time' : 'Elegir día y hora'} <ExternalLink className="w-4 h-4 opacity-70" />
                </a>
                {bookingClicked && (
                  <p className="font-body text-xs text-white/70 mt-3 leading-relaxed" data-testid="post-test-booking-opened">
                    {lang === 'en'
                      ? 'We have opened the calendar in a new tab. Once you book, you will receive the confirmation by email. If it did not open, tap the button again.'
                      : 'Te hemos abierto la agenda en otra pestaña. Cuando reserves, recibirás la confirmación por email. Si no se ha abierto, pulsa de nuevo el botón.'}
                  </p>
                )}
              </div>
            </div>

            {/* Freedy, como opción para resolver dudas antes de la llamada */}
            <div className="px-5 sm:px-6 pt-6 pb-4">
              <p className="flex items-center gap-2 font-body text-sm text-white/80 mb-2">
                <MessageCircle className="w-4 h-4 text-[#D4AF37]" />
                {lang === 'en' ? 'Any questions first? Ask Freedy' : '¿Tienes dudas antes? Pregunta a Freedy'}
              </p>
              <FreedySDRChat
                leadId={sdrLead.leadId}
                leadName={personal.nombre}
                prefillEmail={personal.email}
                temperature={sdrLead.temperature}
                affiliateName={affiliateName}
                bookingInParent
                compact
              />
            </div>
          </div>
        ) : success ? (
          <div className="p-12 text-center animate-in fade-in duration-500" data-testid="qualification-success">
            <div className="w-24 h-24 mx-auto bg-[#0F172A] rounded-full flex items-center justify-center mb-8 animate-in zoom-in duration-500">
              <CheckCircle2 className="w-12 h-12 text-[#D4AF37]" />
            </div>
            <h3 className="font-heading text-3xl text-[#0F172A] mb-3">{qft.success.heading}</h3>
            <p className="font-body text-slate-500 mb-2 max-w-sm mx-auto">{qft.success.desc}</p>
            <Button onClick={handleClose} className="bg-[#0F172A] text-white hover:bg-[#1E293B] px-10 py-3.5 rounded-none font-semibold mt-6" data-testid="qualification-close">{qft.buttons.close}</Button>
          </div>
        ) : (
          <>
            {/* HEADER */}
            <div className="bg-[#0F172A] px-5 sm:px-8 pt-7 pb-5 relative">
              <button type="button" onClick={handleClose} aria-label={qft.buttons.close} className="absolute top-2 right-2 w-10 h-10 flex items-center justify-center text-white/40 hover:text-white transition-colors" data-testid="q-close-x">
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3 mb-3">
                <Shield className="w-5 h-5 text-[#D4AF37]" />
                <DialogHeader className="p-0 space-y-0">
                  <DialogTitle className="font-heading text-xl text-white">{qft.title}</DialogTitle>
                  <DialogDescription className="font-body text-white/40 text-sm">Freedom Homes Club</DialogDescription>
                </DialogHeader>
              </div>
              {step !== 'intro' && step !== 'rejected' && (
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-1 bg-white/10 overflow-hidden rounded-full">
                    <div className="h-full bg-[#D4AF37] rounded-full transition-all duration-700 ease-out" style={{ width: `${progress}%` }} />
                  </div>
                  <span className="font-body text-xs text-white/30 tabular-nums whitespace-nowrap">{displayStep} / {displayTotal}</span>
                </div>
              )}
            </div>

            {/* BODY */}
            <div className="px-5 sm:px-8 py-6 overflow-y-auto max-h-[calc(92vh-140px)]">
              <div key={step} className="animate-in fade-in slide-in-from-right-4 duration-300">

                {/* INTRO */}
                {step === 'intro' && (
                  <div className="space-y-5" data-testid="form-intro">
                    <h3 className="font-heading text-2xl text-[#0F172A]">{qft.intro.heading}</h3>
                    <p className="font-body text-sm text-slate-600 leading-relaxed">{qft.intro.p1}</p>
                    <p className="font-body text-sm text-slate-600 leading-relaxed">{qft.intro.p2}</p>
                    <Button onClick={goNext} className="w-full h-12 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-semibold rounded-none inline-flex items-center justify-center gap-2 text-base" data-testid="q-start-test">
                      {qft.intro.cta} <ArrowRight className="w-5 h-5" />
                    </Button>
                  </div>
                )}

                {/* Q1 */}
                {step === 'q1' && (
                  <QuestionStep num={1} question={qft.q1.question} answerKey="intencion" options={qft.q1.options} />
                )}

                {/* Q2 */}
                {step === 'q2' && (
                  <QuestionStep num={2} question={qft.q2.question} answerKey="perfil" options={qft.q2.options} />
                )}

                {/* Q3 */}
                {step === 'q3' && (
                  <QuestionStep num={3} question={qft.q3.question} answerKey="tiempo" options={qft.q3.options} />
                )}

                {/* Q4 */}
                {step === 'q4' && (
                  <QuestionStep num={4} question={qft.q4.question} answerKey="disponibilidad" options={qft.q4.options} />
                )}

                {/* Q5 */}
                {step === 'q5' && (
                  <div className="space-y-4" data-testid="form-q5">
                    <p className="font-body text-sm font-semibold text-[#0F172A] mb-1">
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-[#0F172A] text-white text-xs rounded-full mr-2">5</span>
                      {qft.q5.heading}
                    </p>
                    <div className="bg-slate-50 border border-slate-200 p-4">
                      <p className="font-body text-sm text-slate-600 leading-relaxed">
                        {qft.q5.p1} <strong className="text-[#0F172A]">{qft.q5.strong}</strong> {qft.q5.p2}
                      </p>
                      <p className="font-body text-sm text-slate-600 leading-relaxed mt-3">{qft.q5.p3}</p>
                      <p className="font-body text-sm text-[#0F172A] font-semibold mt-3">{qft.q5.p4}</p>
                    </div>
                    <div className="space-y-2">
                      {qft.q5.confirmOptions.map((opt, i) => (
                        <OptionBtn key={i} selected={answers.confirmacion === opt} onClick={() => handleAnswer('confirmacion', opt)} testId={`q-confirmacion-${i}`}>{opt}</OptionBtn>
                      ))}
                    </div>
                    <div className="flex gap-3 pt-3"><BackBtn /></div>
                  </div>
                )}

                {/* REJECTED */}
                {step === 'rejected' && (
                  <div className="text-center py-6 space-y-4" data-testid="form-rejected">
                    <div className="w-20 h-20 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-4">
                      <XCircle className="w-10 h-10 text-slate-400" />
                    </div>
                    <h3 className="font-heading text-2xl text-[#0F172A]">{qft.rejected.heading}</h3>
                    <p className="font-body text-sm text-slate-500 max-w-sm mx-auto leading-relaxed">{qft.rejected.desc}</p>
                    <Button onClick={handleClose} className="bg-[#0F172A] text-white hover:bg-[#1E293B] px-10 py-3 rounded-none font-semibold mt-4" data-testid="q-close-rejected">{qft.buttons.close}</Button>
                  </div>
                )}

                {/* FINAL */}
                {step === 'final' && (
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="form-final">
                    <h3 className="font-heading text-2xl text-[#0F172A]">{qft.final.heading}</h3>
                    <p className="font-body text-sm text-slate-600 leading-relaxed">{qft.final.desc}</p>

                    {affiliateName && (
                      <div className="flex items-center gap-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg px-4 py-3" data-testid="referral-sponsor-badge">
                        <UserCheck className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                        <p className="text-sm text-[#0F172A]">{qft.final.invitedBy} <strong className="text-[#D4AF37]">{affiliateName}</strong></p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="font-body text-xs text-slate-500 uppercase tracking-wider mb-1 block">{qft.final.nombre} *</label>
                        <Input value={personal.nombre} onChange={(e) => setPersonal({ ...personal, nombre: e.target.value })}
                          className="h-12 rounded-none border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20" placeholder={qft.final.nombrePh} required data-testid="q-nombre" />
                      </div>
                      <div>
                        <label className="font-body text-xs text-slate-500 uppercase tracking-wider mb-1 block">{qft.final.apellidos} *</label>
                        <Input value={personal.apellidos} onChange={(e) => setPersonal({ ...personal, apellidos: e.target.value })}
                          className="h-12 rounded-none border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20" placeholder={qft.final.apellidosPh} required data-testid="q-apellidos" />
                      </div>
                    </div>
                    <div>
                      <label className="font-body text-xs text-slate-500 uppercase tracking-wider mb-1 block">{qft.final.whatsapp} *</label>
                      <PhoneInput value={personal.whatsapp} onChange={(v) => setPersonal({ ...personal, whatsapp: v })} required testId="q-whatsapp" ariaLabel={qft.final.whatsapp} />
                    </div>
                    <div>
                      <label className="font-body text-xs text-slate-500 uppercase tracking-wider mb-1 block">{qft.final.email} *</label>
                      <Input type="email" value={personal.email} onChange={(e) => setPersonal({ ...personal, email: e.target.value })}
                        className="h-12 rounded-none border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20" placeholder="tu@email.com" required data-testid="q-email" />
                    </div>

                    <div>
                      <label className="font-body text-xs text-slate-500 uppercase tracking-wider mb-2 block">{qft.final.horarioLabel} *</label>
                      <div className="space-y-2">
                        {qft.final.horarioOptions.map((opt, i) => (
                          <OptionBtn key={i} selected={horario === opt} onClick={() => setHorario(opt)} testId={`q-horario-${i}`}>{opt}</OptionBtn>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-slate-100 pt-4 space-y-3">
                      <label className="flex items-start gap-3 cursor-pointer" data-testid="q-policy">
                        <input type="checkbox" checked={acceptPolicy} onChange={(e) => setAcceptPolicy(e.target.checked)}
                          className="mt-0.5 h-5 w-5 rounded border-slate-300 text-[#D4AF37] focus:ring-[#D4AF37] cursor-pointer" />
                        <span className="font-body text-sm text-slate-500 leading-relaxed">
                          {qft.final.policyText}{' '}
                          <a href="/privacy-policy-fhc" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] underline hover:text-[#C5A028]">{qft.final.policyLink}</a>
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-testid="q-contact-consent">
                        <input type="checkbox" checked={acceptContact} onChange={(e) => setAcceptContact(e.target.checked)}
                          className="mt-0.5 h-5 w-5 rounded border-slate-300 text-[#D4AF37] focus:ring-[#D4AF37] cursor-pointer" />
                        <span className="font-body text-sm text-slate-500 leading-relaxed">{qft.final.contactText}</span>
                      </label>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <Button type="button" onClick={goBack} variant="outline" className="h-12 px-4 sm:px-6 rounded-none border-slate-200 text-slate-600 hover:bg-slate-50 inline-flex items-center gap-2" data-testid="q-back-final">
                        <ArrowLeft className="w-4 h-4" /> {qft.buttons.back}
                      </Button>
                      <Button type="submit" disabled={loading || !acceptPolicy || !acceptContact || !horario}
                        className="flex-1 h-12 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-semibold rounded-none text-base inline-flex items-center justify-center gap-2 disabled:opacity-40" data-testid="q-submit">
                        {loading ? (
                          <span className="inline-flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-[#0F172A]/30 border-t-[#0F172A] rounded-full animate-spin" />
                            {qft.final.submitting}
                          </span>
                        ) : (
                          <>{qft.final.submit} <Sparkles className="w-4 h-4" /></>
                        )}
                      </Button>
                    </div>
                  </form>
                )}

              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
